/**
 * Opens and closes Expert, FAQ, recipe, and product rows by height.
 * Background art is pinned first, so the photo and the plant stay put
 * while the row eases open. 320ms.
 */
(function () {
  const ITEM = '.powdi-faq__item, .powdi-expertise__item, .powdi-faq-page__item, .powdi-pfaq__item, .powdi-precipe__item, .powdi-product__accordion';
  const PANEL = '.powdi-faq__panel, .powdi-expertise__panel, .powdi-faq-page__panel, .powdi-pfaq__panel, .powdi-precipe__panel, .powdi-product__accordion-body';
  const DURATION = 320;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function panelOf(item) {
    return item.querySelector(PANEL);
  }

  function positionRatio(token) {
    if (!token) return 0.5;
    if (token === 'left' || token === 'top') return 0;
    if (token === 'center') return 0.5;
    if (token === 'right' || token === 'bottom') return 1;
    if (token.endsWith('%')) return parseFloat(token) / 100;
    return 0.5;
  }

  function pinPlant(plant) {
    const section = plant.closest('.powdi-faq');
    if (!section) return;
    const top = plant.getBoundingClientRect().top - section.getBoundingClientRect().top;
    plant.style.top = top + 'px';
    plant.style.transform = 'translateX(-50%)';
  }

  function pinPhoto(img) {
    if (!img.naturalWidth) {
      img.addEventListener('load', () => pinPhoto(img), { once: true });
      return;
    }
    const frame = img.parentElement.getBoundingClientRect();
    if (frame.width < 1 || frame.height < 1) return;
    const scale = Math.max(frame.width / img.naturalWidth, frame.height / img.naturalHeight);
    const renderedW = img.naturalWidth * scale;
    const renderedH = img.naturalHeight * scale;
    const parts = getComputedStyle(img).objectPosition.split(/\s+/);
    const extraX = renderedW - frame.width;
    const extraY = renderedH - frame.height;
    img.style.position = 'absolute';
    img.style.width = renderedW + 'px';
    img.style.height = renderedH + 'px';
    img.style.maxWidth = 'none';
    img.style.objectFit = 'fill';
    img.style.left = -extraX * positionRatio(parts[0]) + 'px';
    img.style.top = -extraY * positionRatio(parts[1]) + 'px';
  }

  function clearPins(root) {
    (root || document).querySelectorAll('.powdi-faq__plant, .powdi-expertise__bg-img').forEach((node) => {
      node.style.top = '';
      node.style.left = '';
      node.style.width = '';
      node.style.height = '';
      node.style.transform = '';
      node.style.maxWidth = '';
      node.style.position = '';
      node.style.objectFit = '';
    });
  }

  function pinBackgrounds(root) {
    (root || document).querySelectorAll('.powdi-faq__plant').forEach(pinPlant);
    (root || document).querySelectorAll('.powdi-expertise__bg-img').forEach(pinPhoto);
  }

  function restMargin(panel) {
    const inline = panel.style.marginTop;
    panel.style.marginTop = '';
    const value = getComputedStyle(panel).marginTop || '0px';
    panel.style.marginTop = inline;
    return value;
  }

  function measure(panel) {
    const previous = panel.style.height;
    panel.style.height = 'auto';
    const height = panel.scrollHeight;
    panel.style.height = previous;
    return height;
  }

  function play(panel, from, to, marginFrom, marginTo) {
    panel.style.overflow = 'hidden';
    const animation = panel.animate(
      [
        { height: from + 'px', marginTop: marginFrom },
        { height: to + 'px', marginTop: marginTo },
      ],
      { duration: DURATION, easing: EASING, fill: 'forwards' }
    );
    panel._powdiAnim = animation;
    return animation;
  }

  function clear(panel) {
    panel._powdiAnim = null;
    panel.getAnimations().forEach((animation) => animation.cancel());
    panel.style.height = '';
    panel.style.marginTop = '';
    panel.style.overflow = '';
    panel._powdiAnim = null;
  }

  function animateOpen(item) {
    const panel = panelOf(item);
    if (!panel) {
      item.open = true;
      return;
    }
    const from = item.open ? panel.getBoundingClientRect().height : 0;
    item.classList.remove('is-closing');
    panel.style.overflow = 'hidden';
    panel.style.height = from + 'px';
    panel.style.marginTop = from === 0 ? '0px' : panel.style.marginTop;
    panel._powdiAnim = null;
    panel.getAnimations().forEach((animation) => animation.cancel());
    item.open = true;
    const margin = restMargin(panel);
    const target = measure(panel);
    const animation = play(panel, from, target, from === 0 ? '0px' : margin, margin);
    animation.onfinish = () => {
      if (panel._powdiAnim !== animation) return;
      clear(panel);
    };
  }

  function animateClose(item) {
    const panel = panelOf(item);
    if (!panel || !item.open) return;
    const from = panel.getBoundingClientRect().height;
    item.classList.add('is-closing');
    panel.style.overflow = 'hidden';
    panel.style.height = from + 'px';
    panel._powdiAnim = null;
    panel.getAnimations().forEach((animation) => animation.cancel());
    const margin = restMargin(panel);
    const animation = play(panel, from, 0, margin, '0px');
    animation.onfinish = () => {
      if (panel._powdiAnim !== animation) return;
      item.open = false;
      item.classList.remove('is-closing');
      clear(panel);
    };
  }

  function bind(item) {
    if (item.dataset.powdiAccordion === '1') return;
    const summary = item.querySelector('summary');
    if (!summary) return;
    item.dataset.powdiAccordion = '1';
    item.removeAttribute('name');
    summary.addEventListener('click', (event) => {
      const list = item.parentElement;
      const multi = list && list.hasAttribute('data-powdi-accordion-multi');
      if (reduced()) {
        if (!item.open && list && !multi) {
          list.querySelectorAll(ITEM).forEach((other) => {
            if (other !== item) other.open = false;
          });
        }
        return;
      }
      event.preventDefault();
      if (item.open) {
        animateClose(item);
        return;
      }
      if (list && !multi) {
        list.querySelectorAll(ITEM).forEach((other) => {
          if (other !== item && other.open) animateClose(other);
        });
      }
      animateOpen(item);
    });
  }

  function boot(root) {
    (root || document).querySelectorAll(ITEM).forEach(bind);
    pinBackgrounds(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  window.addEventListener('load', () => pinBackgrounds(document));

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      clearPins(document);
      requestAnimationFrame(() => pinBackgrounds(document));
    }, 150);
  });

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
